import { useState, useEffect, useCallback } from 'react';
import { sb } from '../../lib/supabase';
import { useSupabaseAuth, useAccess } from '../../context/AuthContext';
import Loader from '../../components/Loader';
import Modal from '../../components/Modal';
import { COST_CATEGORY_LABEL, BILLING_FREQUENCY_LABEL } from '../../lib/costEngine';

const CATEGORIES = Object.keys(COST_CATEGORY_LABEL);
const FREQUENCIES = Object.keys(BILLING_FREQUENCY_LABEL);

const BLANK = {
  code: '',
  name: '',
  category: 'licensing',
  default_unit: '',
  default_billing_frequency: 'annual',
  default_rate: '',
  default_escalation_pct: '3',
  is_active: true,
};

function CostTypeModal({ item, orgId, onSave, onClose, saving }) {
  const [form, setForm] = useState(item ? { ...item, default_rate: item.default_rate ?? '', default_escalation_pct: item.default_escalation_pct ?? '' } : { ...BLANK });

  return (
    <Modal
      title={item ? 'Edit cost type' : 'New org cost type'}
      onClose={onClose}
      busy={saving}
      footer={(
        <>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-primary" disabled={saving || !form.code.trim() || !form.name.trim()} onClick={() => onSave(form)}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      )}
    >
      <div className="form-2col">
        <div className="form-row">
          <label className="form-label">Code *</label>
          <input className="form-input" value={form.code} disabled={!!item} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))} />
        </div>
        <div className="form-row">
          <label className="form-label">Name *</label>
          <input className="form-input" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
        </div>
      </div>
      <div className="form-2col">
        <div className="form-row">
          <label className="form-label">Category</label>
          <select className="form-select" value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{COST_CATEGORY_LABEL[c]}</option>)}
          </select>
        </div>
        <div className="form-row">
          <label className="form-label">Default unit</label>
          <input className="form-input" value={form.default_unit || ''} onChange={(e) => setForm((p) => ({ ...p, default_unit: e.target.value }))} />
        </div>
      </div>
      <div className="form-2col">
        <div className="form-row">
          <label className="form-label">Billing frequency</label>
          <select className="form-select" value={form.default_billing_frequency || 'annual'} onChange={(e) => setForm((p) => ({ ...p, default_billing_frequency: e.target.value }))}>
            {FREQUENCIES.map((f) => <option key={f} value={f}>{BILLING_FREQUENCY_LABEL[f]}</option>)}
          </select>
        </div>
        <div className="form-row">
          <label className="form-label">Guide rate</label>
          <input className="form-input" type="number" value={form.default_rate} onChange={(e) => setForm((p) => ({ ...p, default_rate: e.target.value }))} />
        </div>
      </div>
      <div className="form-row">
        <label className="form-label">Default escalation %</label>
        <input className="form-input" type="number" step="0.1" value={form.default_escalation_pct} onChange={(e) => setForm((p) => ({ ...p, default_escalation_pct: e.target.value }))} />
      </div>
    </Modal>
  );
}

export default function CostTypes() {
  const { profile } = useSupabaseAuth();
  const { isAdmin } = useAccess();
  const orgId = profile?.organisation_id;

  const [platformTypes, setPlatformTypes] = useState([]);
  const [orgTypes, setOrgTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await sb.from('cost_types').select('*').order('organisation_id', { nullsFirst: true }).order('name');
      if (error) throw error;
      setPlatformTypes((data || []).filter((t) => !t.organisation_id));
      setOrgTypes((data || []).filter((t) => t.organisation_id === orgId));
    } catch (e) {
      showToast(e.message || 'Failed to load cost types');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  async function saveType(form) {
    if (!orgId) return;
    setSaving(true);
    try {
      const payload = {
        organisation_id: orgId,
        code: form.code.trim(),
        name: form.name.trim(),
        category: form.category,
        default_unit: form.default_unit || null,
        default_billing_frequency: form.default_billing_frequency || null,
        default_rate: form.default_rate !== '' ? Number(form.default_rate) : null,
        default_escalation_pct: form.default_escalation_pct !== '' ? Number(form.default_escalation_pct) : null,
        is_active: form.is_active !== false,
      };
      if (modal?.id) {
        const { error } = await sb.from('cost_types').update(payload).eq('id', modal.id);
        if (error) throw error;
      } else {
        const { error } = await sb.from('cost_types').insert(payload);
        if (error) throw error;
      }
      setModal(null);
      await load();
      showToast('Cost type saved', 'success');
    } catch (e) {
      showToast(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(row) {
    setSaving(true);
    try {
      const { error } = await sb.from('cost_types').update({ is_active: !row.is_active }).eq('id', row.id);
      if (error) throw error;
      await load();
    } catch (e) {
      showToast(e.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Loader text="Loading cost types…" />;

  if (!isAdmin) {
    return <div className="content"><div className="empty" style={{ marginTop: 40 }}>Company admin access required.</div></div>;
  }

  function renderTable(rows, readOnly = false) {
    return (
      <table className="req-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Name</th>
            <th>Category</th>
            <th>Unit</th>
            <th>Rate</th>
            <th>Active</th>
            {!readOnly && <th />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="req-ref">{row.code}</td>
              <td>{row.name}</td>
              <td>{COST_CATEGORY_LABEL[row.category] || row.category}</td>
              <td style={{ fontSize: 11 }}>{row.default_unit || '—'}</td>
              <td>{row.default_rate != null ? Number(row.default_rate).toLocaleString() : '—'}</td>
              <td>{row.is_active ? 'Yes' : 'No'}</td>
              {!readOnly && (
                <td>
                  <button type="button" className="btn-ghost" style={{ fontSize: 10 }} onClick={() => setModal(row)}>Edit</button>
                  <button type="button" className="btn-ghost" style={{ fontSize: 10 }} onClick={() => toggleActive(row)}>
                    {row.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div className="content ed-admin-body">
      <div className="chdr" style={{ marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 18, margin: 0 }}>Cost type reference</h1>
          <p style={{ fontSize: 12, color: '#888780', marginTop: 4 }}>ADMIN — recurring and one-time cost categories for solution option ROM</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setModal({})}>Add org override</button>
      </div>

      <div className="card" style={{ padding: 12, marginBottom: 12 }}>
        <div className="chdr"><span className="ctitle">Platform defaults (read-only)</span></div>
        {platformTypes.length ? renderTable(platformTypes, true) : <p style={{ fontSize: 12, color: '#888780' }}>No platform defaults seeded.</p>}
      </div>

      <div className="card" style={{ padding: 12 }}>
        <div className="chdr"><span className="ctitle">Organisation cost types</span></div>
        {orgTypes.length ? renderTable(orgTypes) : <p style={{ fontSize: 12, color: '#888780' }}>No org-specific types — platform defaults apply.</p>}
      </div>

      {modal && (
        <CostTypeModal
          item={modal.id ? modal : null}
          orgId={orgId}
          saving={saving}
          onClose={() => setModal(null)}
          onSave={saveType}
        />
      )}

      {toast && (
        <div className={toast.type === 'success' ? 'toast-success' : 'toast-error'}>{toast.msg}</div>
      )}
    </div>
  );
}
